import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { KeyRound, Lock } from 'lucide-react';
import { connectAzure, prepareAzure, testAzureStateBackend } from '../lib/api';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Alert } from './ui/alert';

/**
 * Guided Azure connect (A0) — the keyless twin of the AWS role / GCP WIF modals. Runs
 * one `az` script in the customer's subscription that consents to the read-only Lizrd
 * app and grants Reader + Cost Management Reader, then completes the connection with the
 * (non-secret) tenant + subscription ids. No key or secret is ever created or stored.
 */
export function AzureSetupModal({
  envId,
  onClose,
  onConnected,
}: {
  envId: string;
  onClose: () => void;
  onConnected: () => void;
}) {
  const [tenantId, setTenantId] = useState('');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [regions, setRegions] = useState('');
  const [stateAccount, setStateAccount] = useState('');
  const [stateContainer, setStateContainer] = useState('');
  const [stateKey, setStateKey] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Optional azurerm blob Terraform-state backend, if the customer fills it in.
  const stateBackend =
    stateAccount.trim() && stateContainer.trim() && stateKey.trim()
      ? {
          storageAccount: stateAccount.trim(),
          container: stateContainer.trim(),
          key: stateKey.trim(),
        }
      : undefined;

  const prep = useQuery({
    queryKey: ['azure-prepare', envId],
    queryFn: () => prepareAzure(envId),
    staleTime: Infinity,
    retry: false,
  });

  const connect = useMutation({
    mutationFn: () =>
      connectAzure(envId, {
        tenantId: tenantId.trim(),
        subscriptionId: subscriptionId.trim(),
        regions: regions.trim() ? regions.split(',').map((r) => r.trim()) : undefined,
        stateBackend,
      }),
    onSuccess: (integration) => {
      if (integration.status === 'ERROR') {
        setError(
          integration.lastError ??
            'Could not read the subscription. Check the consent + role grants and retry.',
        );
        return;
      }
      onConnected();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Failed to connect Azure.'),
  });

  const testState = useMutation({
    mutationFn: () =>
      testAzureStateBackend(envId, {
        tenantId: tenantId.trim(),
        subscriptionId: subscriptionId.trim(),
        stateBackend: {
          storageAccount: stateAccount.trim(),
          container: stateContainer.trim(),
          key: stateKey.trim(),
        },
      }),
  });

  const copyScript = () => {
    if (!prep.data) return;
    void navigator.clipboard.writeText(prep.data.setupScript).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  const ready = !!prep.data && !!tenantId.trim() && !!subscriptionId.trim();

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <Card
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2 className="font-display text-xl font-bold">Connect Azure</h2>
        <p className="mt-2 text-sm text-muted">
          Run one script in your subscription to grant Lizrd read-only access — keyless, via
          workload-identity federation. You stay in control and can revoke it any time.
        </p>

        <ul className="mt-4 space-y-2 text-sm">
          <li className="flex gap-2">
            <Lock className="h-4 w-4" aria-hidden />
            <span>
              <strong className="text-bone">Read-only.</strong> Lizrd is granted only{' '}
              <code className="rounded-sm bg-surface-2 px-1">Reader</code> +{' '}
              <code className="rounded-sm bg-surface-2 px-1">Cost Management Reader</code> — it
              describes your resources and reads metrics/cost, never modifies anything and never
              reads your data (no storage contents, no database rows, no Key Vault secrets).
            </span>
          </li>
          <li className="flex gap-2">
            <KeyRound className="h-4 w-4" aria-hidden />
            <span>
              <strong className="text-bone">No keys, ever.</strong> Lizrd authenticates as its own
              identity via OIDC federation; short-lived tokens are minted on demand. No client
              secret or certificate is created, downloaded, or stored.
            </span>
          </li>
          <li className="flex gap-2">
            <span aria-hidden>↩️</span>
            <span>
              <strong className="text-bone">Revoke any time.</strong> Remove the role assignments
              (or the Lizrd app’s service principal) in your tenant and access ends immediately.
            </span>
          </li>
        </ul>

        {prep.isError && (
          <Alert variant="danger" className="mt-4">
            Azure onboarding isn’t configured on this server.
          </Alert>
        )}

        {prep.data && (
          <div className="mt-5 space-y-4">
            <ol className="list-decimal space-y-3 pl-5 text-sm">
              <li>
                In{' '}
                <a
                  href="https://portal.azure.com/#cloudshell/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-iris underline"
                >
                  Azure Cloud Shell
                </a>
                , run this script — it consents to the read-only Lizrd app (
                <code className="rounded-sm bg-surface-2 px-1">{prep.data.appClientId}</code>) and
                grants it Reader + Cost Management Reader on the subscription you choose.
                <div className="mt-2 rounded-sm border border-border bg-surface-2/40">
                  <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
                    <span className="text-xs text-muted">setup.sh</span>
                    <Button variant="outline" size="sm" onClick={copyScript}>
                      {copied ? 'Copied ✓' : 'Copy'}
                    </Button>
                  </div>
                  <pre className="max-h-48 overflow-auto px-3 py-2 text-xs leading-relaxed text-bone">
                    {prep.data.setupScript}
                  </pre>
                </div>
              </li>
              <li>
                Paste the two values the script prints.
                <div className="mt-2 space-y-2">
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">Tenant ID</span>
                    <Input
                      placeholder="00000000-0000-0000-0000-000000000000"
                      value={tenantId}
                      onChange={(e) => setTenantId(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">Subscription ID</span>
                    <Input
                      placeholder="00000000-0000-0000-0000-000000000000"
                      value={subscriptionId}
                      onChange={(e) => setSubscriptionId(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">
                      Regions (optional, comma-separated — blank = all)
                    </span>
                    <Input
                      placeholder="eastus, westeurope"
                      value={regions}
                      onChange={(e) => setRegions(e.target.value)}
                    />
                  </label>
                </div>
              </li>
            </ol>

            <details className="rounded-sm border border-border bg-surface-2/40 px-3 py-2 text-sm">
              <summary className="cursor-pointer font-semibold text-bone">
                Advanced (optional): Terraform state
              </summary>
              <div className="mt-3 space-y-2">
                <p className="text-xs text-muted">
                  Point Lizrd at your <code>terraform.tfstate</code> in an azurerm blob for exact
                  code↔cloud correlation. Read-only; the state is never stored. You can also set
                  this later in the domain’s settings.
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">Storage account</span>
                    <Input
                      placeholder="acmetfstate"
                      value={stateAccount}
                      onChange={(e) => setStateAccount(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">Container</span>
                    <Input
                      placeholder="tfstate"
                      value={stateContainer}
                      onChange={(e) => setStateContainer(e.target.value)}
                    />
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-xs text-muted">Blob key / path</span>
                    <Input
                      placeholder="prod.terraform.tfstate"
                      value={stateKey}
                      onChange={(e) => setStateKey(e.target.value)}
                    />
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!ready || !stateBackend || testState.isPending}
                    onClick={() => testState.mutate()}
                  >
                    {testState.isPending ? 'Testing…' : 'Test read'}
                  </Button>
                  {testState.data &&
                    (testState.data.ok ? (
                      <span className="text-xs text-iris">
                        ✓ Read {testState.data.resourceCount} resources from state
                      </span>
                    ) : (
                      <span className="text-xs text-danger">{testState.data.message}</span>
                    ))}
                  {testState.isError && (
                    <span className="text-xs text-danger">Test failed — check the values.</span>
                  )}
                </div>
              </div>
            </details>

            {error && <Alert variant="danger">{error}</Alert>}
          </div>
        )}

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!ready || connect.isPending}
            onClick={() => {
              setError(null);
              connect.mutate();
            }}
          >
            {connect.isPending ? 'Connecting…' : 'Connect'}
          </Button>
        </div>
      </Card>
    </div>
  );
}
